import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Paper,
  Typography,
  Box,
  Grid,
  Button,
  Chip,
  LinearProgress,
  Card,
  CardContent,
  Divider,
  IconButton,
  Tooltip,
  Alert,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import CalculateIcon from '@mui/icons-material/Calculate';
import UploadIcon from '@mui/icons-material/Upload';
import DoneIcon from '@mui/icons-material/Done';
import ReplayIcon from '@mui/icons-material/Replay';
import AddIcon from '@mui/icons-material/Add';
import PrintIcon from '@mui/icons-material/Print';
import SearchIcon from '@mui/icons-material/Search';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import PersonAddIcon from '@mui/icons-material/PersonAdd';

import { PayrollMonthService, PayrollItemService } from '../../services/payrollService';
import { PayrollMonth, PayrollItem, PayrollSummary, PayrollStatus } from '../../types/payroll';
import { formatCurrency, formatDate } from '../../utils/formatters';
import { useRole } from '../../hooks/useRole';
import ConfirmDialog from '../common/ConfirmDialog';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`payroll-tabpanel-${index}`}
      aria-labelledby={`payroll-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ pt: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

const PayrollMonthDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { hasAnyRole } = useRole();
  
  const [payrollMonth, setPayrollMonth] = useState<PayrollMonth | null>(null);
  const [payrollItems, setPayrollItems] = useState<PayrollItem[]>([]);
  const [payrollSummary, setPayrollSummary] = useState<PayrollSummary | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [confirmAction, setConfirmAction] = useState<string | null>(null);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState<boolean>(false);
  const [tabValue, setTabValue] = useState<number>(0);
  
  // Handle both uppercase and lowercase status values
  const normalizedStatus = payrollMonth?.status?.toLowerCase();
  const canEdit = hasAnyRole(['payroll', 'hr', 'admin']) && (normalizedStatus === 'DRAFT' || normalizedStatus === 'PROCESSING');
  const canSubmit = hasAnyRole(['payroll', 'hr', 'admin']) && (normalizedStatus === 'DRAFT' || normalizedStatus === 'PROCESSING');
  const canApprove = hasAnyRole(['admin']) && normalizedStatus === 'SUBMITTED';
  const canRevert = hasAnyRole(['payroll', 'hr', 'admin']) && normalizedStatus === 'SUBMITTED';
  const canDelete = hasAnyRole(['payroll', 'hr', 'admin']) && (normalizedStatus === 'DRAFT' || normalizedStatus === 'PROCESSING');
  
  // Load payroll month details
  useEffect(() => {
    const fetchData = async () => {
      if (!id) return;
      
      try {
        setLoading(true);
        const [monthResponse, itemsResponse] = await Promise.all([
          PayrollMonthService.getPayrollMonthById(Number(id)),
          PayrollItemService.getAllForMonth(Number(id))
        ]);
        
        setPayrollMonth(monthResponse.data);
        setPayrollItems(itemsResponse.data);
        
        // Only fetch summary if not in DRAFT or PROCESSING status
        const status = monthResponse.data.status?.toLowerCase();
        if (status !== 'DRAFT' && status !== 'PROCESSING') {
          const summaryResponse = await PayrollMonthService.getPayrollSummary(Number(id));
          setPayrollSummary(summaryResponse.data);
        }
        
        setError(null);
      } catch (err: any) {
        setError(err.response?.data?.message || 'Failed to load payroll month details');
        console.error('Error fetching payroll month details:', err);
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
  }, [id]);
  
  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };
  
  const handleBack = () => {
    navigate('/payroll/months');
  };
  
  const handleEdit = () => {
    navigate(`/payroll/months/${id}/edit`);
  };
  
  const handleAddEmployee = () => {
    navigate(`/payroll/months/${id}/add-employee`);
  };
  
  const handleCalculateAll = async () => {
    try {
      setLoading(true);
      const response = await PayrollItemService.calculateAllForMonth(Number(id));
      setPayrollItems(response.data);
      setError(null);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to calculate payroll items');
      console.error('Error calculating payroll items:', err);
    } finally {
      setLoading(false);
    }
  };
  
  const handleSubmit = () => {
    setConfirmAction('submit');
    setConfirmDialogOpen(true);
  };
  
  const handleApprove = () => {
    setConfirmAction('approve');
    setConfirmDialogOpen(true);
  };
  
  const handleRevert = () => {
    setConfirmAction('revert');
    setConfirmDialogOpen(true);
  };
  
  const handleDelete = () => {
    setConfirmAction('delete');
    setConfirmDialogOpen(true);
  };
  
  const handleConfirmAction = async () => {
    if (!id) return;
    
    try {
      setLoading(true);
      
      switch (confirmAction) {
        case 'submit':
          await PayrollMonthService.submitPayrollMonth(Number(id));
          break;
        case 'approve':
          await PayrollMonthService.approvePayrollMonth(Number(id));
          break;
        case 'revert':
          await PayrollMonthService.revertToDraft(Number(id));
          break;
        case 'delete':
          await PayrollMonthService.deletePayrollMonth(Number(id));
          navigate('/payroll/months');
          return;
      }
      
      // Reload the payroll month
      const monthResponse = await PayrollMonthService.getPayrollMonthById(Number(id));
      setPayrollMonth(monthResponse.data);
      
      setConfirmDialogOpen(false);
      setConfirmAction(null);
      setError(null);
    } catch (err: any) {
      setError(err.response?.data?.message || `Failed to ${confirmAction} payroll month`);
      console.error(`Error during ${confirmAction} action:`, err);
    } finally {
      setLoading(false);
    }
  };
  
  const handleCancelAction = () => {
    setConfirmDialogOpen(false);
    setConfirmAction(null);
  };
  
  const handleEditItem = (itemId: number) => {
    navigate(`/payroll/items/${itemId}/edit`);
  };
  
  const handleRemoveEmployee = async (itemId: number) => {
    try {
      setLoading(true);
      await PayrollItemService.removeEmployeeFromPayroll(itemId);
      
      // Refresh payroll items after removal
      const itemsResponse = await PayrollItemService.getAllForMonth(Number(id));
      setPayrollItems(itemsResponse.data);
      
      setError(null);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to remove employee from payroll');
      console.error('Error removing employee from payroll:', err);
    } finally {
      setLoading(false);
    }
  };
  
  const getStatusChip = (status: PayrollStatus) => {
    const normalizedStatus = status?.toLowerCase();
    
    switch(normalizedStatus) {
      case 'DRAFT':
        return <Chip label={status} color="default" />;
      case 'PROCESSING': 
        return <Chip label={status} color="default" />;
      case 'SUBMITTED': 
        return <Chip label={status} color="primary" />;
      case 'COMPLETED':
      case 'APPROVED': 
        return <Chip label={status} color="success" />;
      case 'REJECTED':
        return <Chip label={status} color="error" />;
      default: 
        return <Chip label={status} />;
    }
  };
  
  const getConfirmDialogContent = () => {
    switch (confirmAction) {
      case 'submit':
        return {
          title: 'Submit Payroll Month',
          content: 'Are you sure you want to submit this payroll month for approval? No further edits will be allowed unless reverted back to draft status.'
        };
      case 'approve':
        return {
          title: 'Approve Payroll Month',
          content: 'Are you sure you want to approve this payroll month? This action will finalize the payroll and cannot be undone.'
        };
      case 'revert':
        return {
          title: 'Revert to Draft',
          content: 'Are you sure you want to revert this payroll month back to draft status? This will allow for further edits.'
        };
      case 'delete':
        return {
          title: 'Delete Payroll Month',
          content: 'Are you sure you want to delete this payroll month? This action cannot be undone and will remove all related payroll items.'
        };
      default:
        return {
          title: 'Confirm Action',
          content: 'Are you sure you want to proceed with this action?'
        };
    }
  };
  
  // Calculate summary totals if not available from the API
  const calculatedSummary = useMemo(() => {
    if (payrollSummary) return payrollSummary;
    
    return payrollItems.reduce((acc, item) => {
      return {
        total_employees: acc.total_employees + 1,
        total_base_salary: acc.total_base_salary + (item.base_salary_vnd || 0),
        total_gross_income: acc.total_gross_income + (item.gross_income_vnd || 0),
        total_si_employee: acc.total_si_employee + (item.si_employee_vnd || 0),
        total_hi_employee: acc.total_hi_employee + (item.hi_employee_vnd || 0),
        total_ui_employee: acc.total_ui_employee + (item.ui_employee_vnd || 0),
        total_pit: acc.total_pit + (item.pit_vnd || 0),
        total_net_income: acc.total_net_income + (item.net_income_vnd || 0)
      };
    }, {
      total_employees: 0,
      total_base_salary: 0,
      total_gross_income: 0,
      total_si_employee: 0,
      total_hi_employee: 0,
      total_ui_employee: 0,
      total_pit: 0,
      total_net_income: 0
    });
  }, [payrollItems, payrollSummary]);
  
  if (!payrollMonth && !loading) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Paper sx={{ p: 3 }}>
          <Typography variant="h5" color="error">
            Payroll month not found
          </Typography>
          <Button 
            startIcon={<ArrowBackIcon />} 
            onClick={handleBack} 
            sx={{ mt: 2 }}
          >
            Back to Payroll Months
          </Button>
        </Paper>
      </Container>
    );
  }
  
  const dialogContent = getConfirmDialogContent();
  
  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Paper sx={{ p: 3 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
          <Box display="flex" alignItems="center">
            <IconButton onClick={handleBack} sx={{ mr: 1 }}>
              <ArrowBackIcon />
            </IconButton>
            <Typography variant="h5" component="h1">
              {payrollMonth?.period && new Date(payrollMonth.period + '-01').toLocaleDateString('en-US', { year: 'numeric', month: 'long' })} Payroll
            </Typography>
            {payrollMonth?.status && (
              <Box ml={2}>
                {getStatusChip(payrollMonth.status)}
              </Box>
            )}
          </Box>
          
          <Box>
            {canEdit && (
              <Button 
                variant="outlined" 
                startIcon={<EditIcon />} 
                onClick={handleEdit}
                sx={{ mr: 1 }}
              >
                Edit
              </Button>
            )}
            {canDelete && (
              <Button 
                variant="outlined" 
                color="error" 
                startIcon={<DeleteIcon />} 
                onClick={handleDelete}
              >
                Delete
              </Button>
            )}
          </Box>
        </Box>
        
        {loading && <LinearProgress sx={{ mb: 3 }} />}
        
        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}
        
        {/* Payroll Month Details */}
        <Grid container spacing={3} mb={4}>
          <Grid component="div" sx={{ gridColumn: { xs: 'span 12', md: 'span 6' } }}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Payroll Details
                </Typography>
                <Divider sx={{ mb: 2 }} />
                
                <Grid container spacing={2}>
                  <Grid component="div" sx={{ gridColumn: 'span 6' }}>
                    <Typography variant="body2" color="textSecondary">
                      Period
                    </Typography>
                    <Typography variant="body1">
                      {payrollMonth?.period}
                    </Typography>
                  </Grid>
                  <Grid component="div" sx={{ gridColumn: 'span 6' }}>
                    <Typography variant="body2" color="textSecondary">
                      Working Days
                    </Typography>
                    <Typography variant="body1">
                      {payrollMonth?.working_days || 0}
                    </Typography>
                  </Grid>
                  <Grid component="div" sx={{ gridColumn: 'span 6' }}>
                    <Typography variant="body2" color="textSecondary">
                      Exchange Rate (VND/USD)
                    </Typography>
                    <Typography variant="body1">
                      {payrollMonth?.vnd_to_usd ? formatCurrency(payrollMonth.vnd_to_usd, 'VND') : '—'}
                    </Typography>
                  </Grid>
                  <Grid component="div" sx={{ gridColumn: 'span 6' }}>
                    <Typography variant="body2" color="textSecondary">
                      Status
                    </Typography>
                    <Typography variant="body1" sx={{ textTransform: 'capitalize' }}>
                      {payrollMonth?.status || '—'}
                    </Typography>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>
          
          <Grid component="div" sx={{ gridColumn: { xs: 'span 12', md: 'span 6' } }}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Deductions & Allowances
                </Typography>
                <Divider sx={{ mb: 2 }} />
                
                <Grid container spacing={2}>
                  <Grid component="div" sx={{ gridColumn: 'span 6' }}>
                    <Typography variant="body2" color="textSecondary">
                      Social Insurance (Employee)
                    </Typography>
                    <Typography variant="body1">
                      {payrollMonth?.si_employee_percent ? `${payrollMonth.si_employee_percent}%` : '—'}
                    </Typography>
                  </Grid>
                  <Grid component="div" sx={{ gridColumn: 'span 6' }}>
                    <Typography variant="body2" color="textSecondary">
                      Health Insurance (Employee)
                    </Typography>
                    <Typography variant="body1">
                      {payrollMonth?.hi_employee_percent ? `${payrollMonth.hi_employee_percent}%` : '—'}
                    </Typography>
                  </Grid>
                  <Grid component="div" sx={{ gridColumn: 'span 6' }}>
                    <Typography variant="body2" color="textSecondary">
                      Unemployment Insurance (Employee)
                    </Typography>
                    <Typography variant="body1">
                      {payrollMonth?.ui_employee_percent ? `${payrollMonth.ui_employee_percent}%` : '—'}
                    </Typography>
                  </Grid>
                  <Grid component="div" sx={{ gridColumn: 'span 6' }}>
                    <Typography variant="body2" color="textSecondary">
                      Personal Deduction
                    </Typography>
                    <Typography variant="body1">
                      {payrollMonth?.personal_deduction ? formatCurrency(payrollMonth.personal_deduction, 'VND') : '—'}
                    </Typography>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
        
        {/* Action buttons */}
        <Box display="flex" justifyContent="space-between" mb={3}>
          <Box>
            {canEdit && (
              <>
                <Button 
                  variant="contained" 
                  color="primary" 
                  startIcon={<PersonAddIcon />} 
                  onClick={handleAddEmployee}
                  sx={{ mr: 1 }}
                >
                  Add Employee
                </Button>
                <Button 
                  variant="contained" 
                  startIcon={<CalculateIcon />} 
                  onClick={handleCalculateAll}
                  sx={{ mr: 1 }}
                >
                  Calculate All
                </Button>
              </>
            )}
          </Box>
          
          <Box>
            {canSubmit && (
              <Button 
                variant="contained" 
                color="primary" 
                startIcon={<UploadIcon />} 
                onClick={handleSubmit}
                sx={{ mr: 1 }}
              >
                Submit for Approval
              </Button>
            )}
            
            {canApprove && (
              <Button 
                variant="contained" 
                color="success" 
                startIcon={<DoneIcon />} 
                onClick={handleApprove}
                sx={{ mr: 1 }}
              >
                Approve Payroll
              </Button>
            )}
            
            {canRevert && (
              <Button 
                variant="outlined" 
                startIcon={<ReplayIcon />} 
                onClick={handleRevert}
              >
                Revert to Draft
              </Button>
            )}
          </Box>
        </Box>
        
        {/* Tabs for different views */}
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs value={tabValue} onChange={handleTabChange} aria-label="payroll tabs">
            <Tab label="Payroll Items" />
            <Tab label="Summary" />
          </Tabs>
        </Box>
        
        {/* Payroll Items Tab */}
        <TabPanel value={tabValue} index={0}>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Employee ID</TableCell>
                  <TableCell>Name</TableCell>
                  <TableCell>Base Salary</TableCell>
                  <TableCell>Gross Income</TableCell>
                  <TableCell>Deductions</TableCell>
                  <TableCell>PIT</TableCell>
                  <TableCell>Net Income</TableCell>
                  <TableCell align="center">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {payrollItems.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} align="center">
                      {loading ? "Loading..." : "No payroll items found. Add employees to the payroll."}
                    </TableCell>
                  </TableRow>
                ) : (
                  payrollItems.map((item) => {
                    const totalDeductions = (item.si_employee_vnd || 0) + 
                                          (item.hi_employee_vnd || 0) + 
                                          (item.ui_employee_vnd || 0);
                    
                    return (
                      <TableRow key={item.id} hover>
                        <TableCell>{item.employee_no}</TableCell>
                        <TableCell>{item.employee_name}</TableCell>
                        <TableCell>{formatCurrency(item.base_salary_vnd, 'VND')}</TableCell>
                        <TableCell>{formatCurrency(item.gross_income_vnd || 0, 'VND')}</TableCell>
                        <TableCell>{formatCurrency(totalDeductions, 'VND')}</TableCell>
                        <TableCell>{formatCurrency(item.pit_vnd || 0, 'VND')}</TableCell>
                        <TableCell>{formatCurrency(item.net_income_vnd || 0, 'VND')}</TableCell>
                        <TableCell align="center">
                          {canEdit && (
                            <Box>
                              <Tooltip title="Edit Item">
                                <IconButton 
                                  size="small" 
                                  onClick={() => handleEditItem(item.id)}
                                >
                                  <EditIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              
                              <Tooltip title="Remove Employee">
                                <IconButton 
                                  size="small" 
                                  color="error"
                                  onClick={() => handleRemoveEmployee(item.id)}
                                >
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </Box>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </TabPanel>
        
        {/* Summary Tab */}
        <TabPanel value={tabValue} index={1}>
          <Grid container spacing={3}>
            <Grid component="div" sx={{ gridColumn: 'span 12' }}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Payroll Summary
                  </Typography>
                  <Divider sx={{ mb: 3 }} />
                  
                  <Grid container spacing={3}>
                    <Grid component="div" sx={{ gridColumn: { xs: 'span 12', sm: 'span 6', md: 'span 4' } }}>
                      <Card variant="outlined" sx={{ mb: 2 }}>
                        <CardContent>
                          <Typography variant="body2" color="textSecondary">
                            Total Employees
                          </Typography>
                          <Typography variant="h5" component="div">
                            {calculatedSummary.total_employees}
                          </Typography>
                        </CardContent>
                      </Card>
                    </Grid>
                    
                    <Grid component="div" sx={{ gridColumn: { xs: 'span 12', sm: 'span 6', md: 'span 4' } }}>
                      <Card variant="outlined" sx={{ mb: 2 }}>
                        <CardContent>
                          <Typography variant="body2" color="textSecondary">
                            Total Base Salary
                          </Typography>
                          <Typography variant="h5" component="div">
                            {formatCurrency(calculatedSummary.total_base_salary, 'VND')}
                          </Typography>
                        </CardContent>
                      </Card>
                    </Grid>
                    
                    <Grid component="div" sx={{ gridColumn: { xs: 'span 12', sm: 'span 6', md: 'span 4' } }}>
                      <Card variant="outlined" sx={{ mb: 2 }}>
                        <CardContent>
                          <Typography variant="body2" color="textSecondary">
                            Total Gross Income
                          </Typography>
                          <Typography variant="h5" component="div">
                            {formatCurrency(calculatedSummary.total_gross_income, 'VND')}
                          </Typography>
                        </CardContent>
                      </Card>
                    </Grid>
                    
                    <Grid component="div" sx={{ gridColumn: { xs: 'span 12', sm: 'span 6', md: 'span 4' } }}>
                      <Card variant="outlined" sx={{ mb: 2 }}>
                        <CardContent>
                          <Typography variant="body2" color="textSecondary">
                            Total Social Insurance
                          </Typography>
                          <Typography variant="h5" component="div">
                            {formatCurrency(calculatedSummary.total_si_employee, 'VND')}
                          </Typography>
                        </CardContent>
                      </Card>
                    </Grid>
                    
                    <Grid component="div" sx={{ gridColumn: { xs: 'span 12', sm: 'span 6', md: 'span 4' } }}>
                      <Card variant="outlined" sx={{ mb: 2 }}>
                        <CardContent>
                          <Typography variant="body2" color="textSecondary">
                            Total PIT
                          </Typography>
                          <Typography variant="h5" component="div">
                            {formatCurrency(calculatedSummary.total_pit, 'VND')}
                          </Typography>
                        </CardContent>
                      </Card>
                    </Grid>
                    
                    <Grid component="div" sx={{ gridColumn: { xs: 'span 12', sm: 'span 6', md: 'span 4' } }}>
                      <Card variant="outlined" sx={{ mb: 2 }}>
                        <CardContent>
                          <Typography variant="body2" color="textSecondary">
                            Total Net Income
                          </Typography>
                          <Typography variant="h5" component="div" color="primary">
                            {formatCurrency(calculatedSummary.total_net_income, 'VND')}
                          </Typography>
                        </CardContent>
                      </Card>
                    </Grid>
                  </Grid>
                  
                  <Box display="flex" justifyContent="flex-end" mt={3}>
                    <Button
                      variant="outlined"
                      startIcon={<PictureAsPdfIcon />}
                      sx={{ mr: 1 }}
                    >
                      Export Summary
                    </Button>
                    <Button
                      variant="outlined"
                      startIcon={<PrintIcon />}
                    >
                      Print Report
                    </Button>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </TabPanel>
      </Paper>
      
      <ConfirmDialog
        open={confirmDialogOpen}
        title={dialogContent.title}
        content={dialogContent.content}
        onConfirm={handleConfirmAction}
        onCancel={handleCancelAction}
      />
    </Container>
  );
};

export default PayrollMonthDetail;
